import type { Session } from "@supabase/auth-js";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

import { authClient, keepsSession } from "./client";
import { forgetExpiry, sessionEnded } from "./expiry";

export type SessionState = { status: "loading" } | { status: "signedOut" } | { status: "signedIn"; session: Session };

const SessionContext = createContext<SessionState>({ status: "loading" });

const signedOut: SessionState = { status: "signedOut" };

/** Follows the session of Supabase Auth, across the tabs of the browser too. A browser that keeps no session is signed
 * out at once, its first screen drawn while the client loads behind it. */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>(() => (keepsSession() ? { status: "loading" } : signedOut));
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let mounted = true;
    void authClient().then((client) => {
      if (!mounted) {
        return;
      }
      let signedIn = false;
      const { data } = client.onAuthStateChange((_event, session) => {
        if (session === null && signedIn) {
          sessionEnded();
        } else if (session !== null) {
          forgetExpiry();
        }
        signedIn = session !== null;
        // Signed out twice is the same state: a new one would clear the tab's drafts again.
        setState((current) =>
          session === null ? (current.status === "signedOut" ? current : signedOut) : { status: "signedIn", session },
        );
      });
      unsubscribe = () => data.subscription.unsubscribe();
    });
    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, []);
  return <SessionContext value={state}>{children}</SessionContext>;
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}
